import { useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GetAppIcon from '@mui/icons-material/GetApp'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import { useInstallPrompt } from './useInstallPrompt'

/**
 * Coordinates the PWA lifecycle UI:
 *  - "New version available" prompt (from the service worker)
 *  - "Ready to work offline" confirmation
 *  - "Install app" prompt (from beforeinstallprompt)
 */
export default function PwaManager() {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW()

  const { canInstall, promptInstall, dismiss } = useInstallPrompt()

  // Auto-hide the "offline ready" confirmation after a short delay.
  useEffect(() => {
    if (!offlineReady) return
    const t = setTimeout(() => setOfflineReady(false), 6000)
    return () => clearTimeout(t)
  }, [offlineReady, setOfflineReady])

  return (
    <>
      {/* Update available */}
      <Snackbar
        open={needRefresh}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={false}
          sx={{ alignItems: 'center', borderRadius: 2 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => updateServiceWorker(true)}
                sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
              >
                Reload
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setNeedRefresh(false)}
                aria-label="Dismiss update"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          A new version of Vericast Events is available.
        </Alert>
      </Snackbar>

      {/* Offline ready */}
      <Snackbar
        open={offlineReady && !needRefresh}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={() => setOfflineReady(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          icon={<CloudDoneIcon />}
          sx={{ borderRadius: 2 }}
          onClose={() => setOfflineReady(false)}
        >
          Ready to use offline.
        </Alert>
      </Snackbar>

      {/* Install prompt */}
      <Snackbar
        open={canInstall && !needRefresh}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 8, sm: 2 } }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(28,85,128,0.15)',
            p: 2,
            maxWidth: 380,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,117,188,0.1)',
              color: 'primary.main',
            }}
          >
            <GetAppIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Install Vericast Events
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add it to your home screen for quick, offline access.
            </Typography>
          </Box>
          <Stack spacing={0.5}>
            <Button size="small" variant="contained" onClick={promptInstall}>
              Install
            </Button>
            <Button size="small" color="inherit" onClick={dismiss}>
              Not now
            </Button>
          </Stack>
        </Box>
      </Snackbar>
    </>
  )
}
